import guid from '../util/guid';
import EventEmitter from './event-emitter';
import { DATA_EVENTS } from './constant';

/**
 * @module subscriber
 */
export default class Subscriber extends EventEmitter {
    constructor(option) {
        super();
        this.id = guid();
        this.securityManager = option.securityManager;
        this.apiManager = option.apiManager;
        this.ready = false;
        this.on(DATA_EVENTS.DATA, res => {
            if (res.data) {
                res.data.forEach(quote => {
                    this.securityManager.setQuotes(res.type, quote);
                });
            }
        });
    }

    /**
     * Subscribe the securities to pull.
     * @function subscribe
     * @param {array} securities - One string contains symbols with comma, or a array contains symbol string.
     * @param {object} cbs - Two callback functions data and error.
     * @param {function} cbs.data - Callback will be fired once the pull data is updated.
     * @param {function} [cbs.error] - Callback will be fired once there is any error about the pull.
     *
     * @example
     * subscriber.subscribe(['XNYS:IBM','XNAS:MSFT'], {
     *     data: function(quotes){
     *         console.log('subscribe', quotes);
     *     },
     *     error: function(){
     *         console.log(error);
     *     }
     * });
     */
    async subscribe(securities, handles = {}, params = {}) {
        let securityArray = await this.securityManager
            .create(securities, params.skipIdService)
            .catch(err => {
                this._executeHandleError(handles, err);
            });
        securityArray = securityArray.filter(
            security => security.quoteInstrument.length > 0
        );
        if (securityArray.length) {
            this.securityManager.watch(this.type, securityArray, handles.data);
            const instruments = this.securityManager.getQuoteInstruments(
                securityArray
            );
            this.emit(DATA_EVENTS.SUBSCRIBE, {
                type: this.type,
                data: instruments,
                updateLastAccessTime: !!params.updateLastAccessTime
            });
        } else {
            this._executeHandleError(
                handles,
                this.securityManager.getInvalidSecuritiesError(securities)
            );
        }
    }

    /**
     * UnSubscribe the securities to pull.
     * @function unsubscribe
     * @param {array} securities - One string contains symbols with comma, or a array contains symbol string.
     * @param {object} cbs - Two callback functions data and error.
     * @param {function} cbs.data - Callback will be fired once the pull data is updated.
     * @param {function} cbs.error - Callback will be fired once there is any error about the pull.
     *
     * @example
     * subscriber.unsubscribe(['XNYS:IBM','XNAS:MSFT'], {
     *     data: function(quotes){
     *         console.log('subscribe', quotes);
     *     },
     *     error: function(){
     *         console.log(error);
     *     }
     * });
     */
    async unsubscribe(securities, handles = {}, params = {}) {
        let securityArray = await this.securityManager
            .create(securities, params.skipIdService)
            .catch(err => {
                this._executeHandleError(handles, err);
            });
        securityArray = securityArray.filter(
            security => security.quoteInstrument.length > 0
        );
        if (securityArray.length) {
            this.securityManager.unwatch(
                this.type,
                securityArray,
                handles.data
            );
            const instruments = this.securityManager.getQuoteInstruments(
                securityArray
            );
            this.emit(DATA_EVENTS.UNSUBSCRIBE, {
                type: this.type,
                data: instruments
            });
        } else {
            this._executeHandleError(
                handles,
                this.securityManager.getInvalidSecuritiesError(securities)
            );
        }
    }
    _generateQuotes(securities, quotes) {
        return quotes.map(quote => {
            const security = securities.filter(
                s => s.quoteInstrument === quote.instrument
            )[0];
            const lastMarket = quote.fields.filter(
                item => item.name === 'lastMarketId'
            );
            let ExtraExchangeInfo;
            if (lastMarket.length > 0) {
                ExtraExchangeInfo = (
                    this.securityManager.getExtraExchangeInfo(
                        lastMarket[0].value
                    ) || {}
                ).shortExchangeName;
                quote.fields.push({
                    name: 'extraExchangeInfo',
                    value: ExtraExchangeInfo
                });
            }
            return {
                dataType: quote.dataType,
                security: security.queryKey,
                quotes: quote.fields
            };
        });
    }
    _executeHandleError(handles, error) {
        if (typeof handles.error === 'function') {
            handles.error(error);
        }
    }
}
